/* Transient messages that slide in, stay a few seconds and slide out. Without a provider (unit tests) they do nothing. */
import { Children, createContext, isValidElement, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

export type ToastTone = 'ok' | 'info' | 'error'
interface ToastItem { id: number; tone: ToastTone; message: ReactNode; leaving: boolean }
type Show = (message: ReactNode, tone?: ToastTone) => void

const ToastContext = createContext<Show>(() => {})
export const useToast = () => useContext(ToastContext)

const SHOW_MS = 5000
const LEAVE_MS = 260

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const next = useRef(1)
  const recent = useRef(new Map<string, number>())
  const dismiss = useCallback((id: number) => {
    setItems((l) => l.map((t) => (t.id === id ? { ...t, leaving: true } : t)))
    setTimeout(() => setItems((l) => l.filter((t) => t.id !== id)), LEAVE_MS)
  }, [])
  const show = useCallback<Show>((message, tone = 'info') => {
    // The same message twice within a moment (React StrictMode runs effects twice in development) shows once.
    const key = `${tone}:${textOf(message)}`
    const at = Date.now()
    if (at - (recent.current.get(key) ?? 0) < 600) return
    recent.current.set(key, at)
    const id = next.current++
    setItems((l) => [...l.slice(-3), { id, tone, message, leaving: false }])
    setTimeout(() => dismiss(id), tone === 'error' ? SHOW_MS * 2 : SHOW_MS)
  }, [dismiss])
  const value = useMemo(() => show, [show])
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts">
        {items.map((t) => (
          <div key={t.id} role={t.tone === 'error' ? 'alert' : 'status'} className={`toast ${t.tone}${t.leaving ? ' leaving' : ''}`}>
            <span className="toast-msg">{t.message}</span>
            <button type="button" className="toast-x" aria-label="Dismiss message" onClick={() => dismiss(t.id)}>×</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function textOf(node: ReactNode): string {
  return Children.toArray(node).map((c) => (isValidElement<{ children?: ReactNode }>(c) ? textOf(c.props.children) : String(c))).join('')
}

/** Drop-in for a banner: shows its message as a toast when it appears and renders nothing in the page. */
export function Notice({ tone = 'info', children }: { tone?: ToastTone; children: ReactNode }) {
  const toast = useToast()
  const text = textOf(children)
  useEffect(() => { toast(children, tone) }, [text, tone]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}
